/**
 * One shape, recoloured live. Comparing `Poso 022` against `Kronos 034` on the same
 * silhouette is the only honest way to compare them — the product photography for each is
 * shot in a different light.
 */
import { useId } from 'react';
import type { JSX } from 'react';

export function SofaSilhouette({ hex }: { hex: string[] }): JSX.Element {
  const base = hex[0] ?? '#8A8F96';
  const second = hex[1] ?? base;
  /*
   * Ids are per instance, not per colour.
   *
   * They used to be derived from the hex, so two silhouettes showing the same fabric
   * emitted the same `id` twice — and once the mobile inline preview existed, that
   * happened the moment a colour was clicked. Duplicate ids resolve to the first match in
   * the document, which was inside the `lg:hidden` copy; a `filter` reference that cannot
   * be resolved means the element is not rendered at all, so the desktop preview went
   * blank the instant it had something to show.
   */
  const uid = useId().replace(/:/g, '');
  const gradientId = `rz-fabric-${uid}`;
  const filterId = `rz-soft-${uid}`;

  return (
    <svg viewBox="0 0 240 150" className="w-full" role="img" aria-label="">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="49.5%" stopColor={base} />
          <stop offset="50.5%" stopColor={second} />
        </linearGradient>
        <filter id={filterId} x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="3" stdDeviation="5" floodOpacity="0.14" />
        </filter>
      </defs>

      <g filter={`url(#${filterId})`} style={{ transition: 'fill 300ms cubic-bezier(0.22,1,0.36,1)' }}>
        {/* backrest */}
        <rect x="24" y="28" width="192" height="46" rx="12" fill={`url(#${gradientId})`} />
        {/* seat */}
        <rect x="18" y="70" width="204" height="34" rx="10" fill={`url(#${gradientId})`} />
        {/* arms */}
        <rect x="10" y="52" width="22" height="54" rx="10" fill={`url(#${gradientId})`} />
        <rect x="208" y="52" width="22" height="54" rx="10" fill={`url(#${gradientId})`} />
        {/* cushion seams, so the shape reads as upholstery and not a block */}
        <line x1="120" y1="72" x2="120" y2="102" stroke="rgb(0 0 0 / 0.16)" strokeWidth="1.5" />
        <line x1="120" y1="30" x2="120" y2="72" stroke="rgb(0 0 0 / 0.12)" strokeWidth="1.5" />
        {/* legs */}
        <rect x="30" y="104" width="7" height="16" rx="2" fill="var(--color-ink)" opacity="0.75" />
        <rect x="203" y="104" width="7" height="16" rx="2" fill="var(--color-ink)" opacity="0.75" />
      </g>
    </svg>
  );
}
